import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FiltersBarProps {
  selectedMoods: string[];
  timeOfDay: 'day' | 'night' | 'all';
  openNow: boolean;
  nearMe: boolean;
  budget: number[];
  selectedCategory: string;
  onMoodToggle: (mood: string) => void;
  onTimeChange: (time: 'day' | 'night' | 'all') => void;
  onOpenNowToggle: () => void;
  onNearMeToggle: () => void;
  onBudgetToggle: (level: number) => void;
  onCategoryChange: (category: string) => void;
}

export default function FiltersBar({
  selectedMoods,
  timeOfDay,
  openNow,
  nearMe,
  budget,
  selectedCategory,
  onMoodToggle,
  onTimeChange,
  onOpenNowToggle,
  onNearMeToggle,
  onBudgetToggle,
  onCategoryChange
}: FiltersBarProps) {
  const { t } = useTranslation();
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const categories = [
    { key: 'all', icon: 'ri-apps-line' },
    { key: 'eat', icon: 'ri-restaurant-2-line' },
    { key: 'party', icon: 'ri-goblet-line' },
    { key: 'pub', icon: 'ri-beer-line' },
    { key: 'explore', icon: 'ri-map-pin-line' },
    { key: 'romantic', icon: 'ri-heart-2-line' },
    { key: 'unique', icon: 'ri-star-line' },
    { key: 'spa', icon: 'ri-hand-heart-line' },
    { key: 'massage', icon: 'ri-mental-health-line' },
    { key: 'coffee', icon: 'ri-cup-line' },
    { key: 'shopping', icon: 'ri-shopping-bag-line' }
  ];

  const moods = [
    { key: 'chill', icon: 'ri-leaf-line' },
    { key: 'eat', icon: 'ri-restaurant-line' },
    { key: 'party', icon: 'ri-music-line' },
    { key: 'romantic', icon: 'ri-heart-line' },
    { key: 'explore', icon: 'ri-compass-line' },
    { key: 'recover', icon: 'ri-spa-line' }
  ];

  // Count active secondary filters
  const activeFilterCount = 
    selectedMoods.length + 
    (timeOfDay !== 'all' ? 1 : 0) + 
    (openNow ? 1 : 0) + 
    (nearMe ? 1 : 0) + 
    (budget.length < 3 ? 1 : 0);

  return (
    <>
      <section className="sticky top-[73px] z-40 bg-white border-b border-gray-100">
        {/* Mobile: Horizontal Scrolling Pills + Filter Button */}
        <div className="md:hidden px-4 py-3 flex items-center gap-2">
          {/* Category Pills - Horizontal Scroll */}
          <div className="flex-1 overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 pb-1">
              {categories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => onCategoryChange(category.key)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
                    selectedCategory === category.key
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <i className={`${category.icon} text-sm`}></i>
                  <span>{t(`categories.${category.key}`)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Filter Button */}
          <button
            onClick={() => setShowFilterPanel(true)}
            className="relative flex items-center justify-center w-10 h-10 rounded-full bg-teal-500 text-white shadow-md cursor-pointer flex-shrink-0"
          >
            <i className="ri-filter-3-line text-lg"></i>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Desktop: Full Expanded Layout */}
        <div className="hidden md:block px-6 py-4">
          <div className="max-w-7xl mx-auto">
            {/* Category Filters */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => onCategoryChange(category.key)}
                  className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl text-xs font-medium transition-all whitespace-nowrap cursor-pointer min-w-[72px] ${
                    selectedCategory === category.key
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${category.icon} text-lg`}></i>
                  </div>
                  <span>{t(`categories.${category.key}`)}</span>
                </button>
              ))}
            </div>

            {/* Mood Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
              {moods.map((mood) => (
                <button
                  key={mood.key}
                  onClick={() => onMoodToggle(mood.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                    selectedMoods.includes(mood.key)
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${mood.icon} text-base`}></i>
                  {t(`moods.${mood.key}`)}
                </button>
              ))}
            </div>

            {/* Other Filters */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Time Toggle */}
              <div className="flex bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => onTimeChange('day')}
                  className={`px-4 py-1 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    timeOfDay === 'day' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  🌞 {t('filters.day')}
                </button>
                <button
                  onClick={() => onTimeChange('night')}
                  className={`px-4 py-1 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    timeOfDay === 'night' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}
                >
                  🌙 {t('filters.night')}
                </button>
              </div>

              {/* Open Now */}
              <button
                onClick={onOpenNowToggle}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  openNow
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-time-line text-base"></i>
                {t('filters.openNow')}
              </button>

              {/* Near Me */}
              <button
                onClick={onNearMeToggle}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  nearMe
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-navigation-line text-base"></i>
                {t('filters.distance')}
              </button>

              {/* Budget */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-2">
                <span className="text-sm font-medium text-gray-700">{t('filters.budget')}:</span>
                {[1, 2, 3].map((level) => (
                  <button
                    key={level}
                    onClick={() => onBudgetToggle(level)}
                    className={`px-2 py-1 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      budget.includes(level)
                        ? 'bg-teal-500 text-white'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {'$'.repeat(level)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filter Panel - Bottom Sheet */}
      {showFilterPanel && (
        <div className="md:hidden fixed inset-0 z-50 flex items-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowFilterPanel(false)}
          ></div>

          {/* Panel */}
          <div className="relative w-full bg-white rounded-t-3xl shadow-2xl max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{t('filters.title') || 'Filters'}</h3>
              <button
                onClick={() => setShowFilterPanel(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer"
              >
                <i className="ri-close-line text-lg text-gray-600"></i>
              </button>
            </div>

            {/* Content */}
            <div className="px-5 py-6 space-y-6">
              {/* Mood Filters */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('filters.mood') || 'Mood'}</h4>
                <div className="flex flex-wrap gap-2">
                  {moods.map((mood) => (
                    <button
                      key={mood.key}
                      onClick={() => onMoodToggle(mood.key)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                        selectedMoods.includes(mood.key)
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      <i className={`${mood.icon} text-base`}></i>
                      {t(`moods.${mood.key}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time of Day */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('filters.timeOfDay') || 'Time of Day'}</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => onTimeChange('day')}
                    className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                      timeOfDay === 'day' ? 'bg-teal-500 text-white shadow-md' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    🌞 {t('filters.day')}
                  </button>
                  <button
                    onClick={() => onTimeChange('night')}
                    className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                      timeOfDay === 'night' ? 'bg-teal-500 text-white shadow-md' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    🌙 {t('filters.night')}
                  </button>
                  <button
                    onClick={() => onTimeChange('all')}
                    className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                      timeOfDay === 'all' ? 'bg-teal-500 text-white shadow-md' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    ⏰ {t('filters.anytime') || 'Anytime'}
                  </button>
                </div>
              </div>

              {/* Quick Filters */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('filters.quick') || 'Quick Filters'}</h4>
                <div className="space-y-2">
                  <button
                    onClick={onOpenNowToggle}
                    className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer flex items-center gap-3 ${
                      openNow ? 'bg-teal-500 text-white shadow-md' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <i className="ri-time-line text-lg"></i>
                    <span className="flex-1 text-left">{t('filters.openNow')}</span>
                    {openNow && <i className="ri-check-line text-lg"></i>}
                  </button>
                  <button
                    onClick={onNearMeToggle}
                    className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer flex items-center gap-3 ${
                      nearMe ? 'bg-teal-500 text-white shadow-md' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <i className="ri-navigation-line text-lg"></i>
                    <span className="flex-1 text-left">{t('filters.distance')}</span>
                    {nearMe && <i className="ri-check-line text-lg"></i>}
                  </button>
                </div>
              </div>

              {/* Budget */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t('filters.budget')}</h4>
                <div className="flex gap-2">
                  {[1, 2, 3].map((level) => (
                    <button
                      key={level}
                      onClick={() => onBudgetToggle(level)}
                      className={`flex-1 px-4 py-3 rounded-xl text-base font-bold transition-all cursor-pointer ${
                        budget.includes(level)
                          ? 'bg-teal-500 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {'$'.repeat(level)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-4 flex gap-3">
              <button
                onClick={() => {
                  onTimeChange('all');
                  onOpenNowToggle();
                  onNearMeToggle();
                  selectedMoods.forEach(mood => onMoodToggle(mood));
                  [1, 2, 3].forEach(level => {
                    if (!budget.includes(level)) onBudgetToggle(level);
                  });
                }}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
              >
                {t('filters.clearAll') || 'Clear All'}
              </button>
              <button
                onClick={() => setShowFilterPanel(false)}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-semibold bg-teal-500 text-white hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap shadow-md"
              >
                {t('filters.apply') || 'Apply Filters'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}